import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Fuel, Database } from "lucide-react";

const BoilerFuelConsumption = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "daily-breakdown",
      title: "Fuel Consumption Breakdown",
      description: "Calculates fuel consumption for each fuel type per day, handling missing data",
      formula: "Daily = {HFO: boilerConsHfo, LFO: boilerConsLfo, MGO: boilerConsMgo, Others: boilerConsOthers}",
      subFormulas: [
        "HFO = record?.boilerConsHfo || 0",
        "LFO = record?.boilerConsLfo || 0",
        "MGO = record?.boilerConsMgo || 0",
        "Others = record?.boilerConsOthers || 0",
        "Daily Total = HFO + LFO + MGO + Others"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },

    {
      id: "period-totals",
      title: "Total Calculations",
      description: "Sums consumption for each fuel type across the entire period",
      formula: "Total_FuelType = Σ(Daily consumption for that fuel type)",
      subFormulas: [
        "Total HFO = Σ(HFO for each day)",
        "Total LFO = Σ(LFO for each day)",
        "Total MGO = Σ(MGO for each day)",
        "Total Others = Σ(Others for each day)",
        "Overall Total = Total HFO + Total LFO + Total MGO + Total Others"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
  ];

  const dataStructure = {
    fields: [
      { name: "boilerConsHfo", type: "Number", description: "Heavy Fuel Oil consumption (kg)" },
      { name: "boilerConsLfo", type: "Number", description: "Light Fuel Oil consumption (kg)" },
      { name: "boilerConsMgo", type: "Number", description: "Marine Gas Oil consumption (kg)" },
      { name: "boilerConsOthers", type: "Number", description: "Other fuel types consumption (kg)" },
    ]
  };

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#cde7f7] rounded-lg">
              <Fuel className="w-6 h-6 text-[#1a5276]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Boiler Fuel Consumption Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Fuel usage analysis and consumption tracking methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <div className="mb-8">
                <h4 className="font-semibold text-gray-700 mb-3">Data Source Structure</h4>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {dataStructure.fields.map((field, index) => (
                      <div key={index} className="bg-white p-3 rounded border">
                        <div className="font-medium text-gray-800">{field.name}</div>
                        <div className="text-sm text-gray-500 mb-1">{field.type}</div>
                        <div className="text-xs text-gray-600">{field.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Primary Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoilerFuelConsumption;